/** Conversational tour inquiry capture for the terminal assistant. */
import { todayISO } from './utils.js';
import { prepareFlowSteps } from './terminal-flow-merge.js';
import { injectCustomSteps } from './terminal-flow-custom.js';
import { mergeCustomFieldsIntoExtra } from './terminal-flow-handlers.js';
import { capitalizeName, isSkip } from './assistant-ticket-new.js';
import { parseSmartDateOnly, validateSmartDate, DATE_HINT } from './assistant-datetime.js';

/** Parse "2/1/0", "2,1,0", or "2" → { adt, chd, inf }. */
export function parsePaxLine(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const parts = s.split(/[/,\s]+/).filter(Boolean);
  if (!parts.length) return null;
  const adt = Number(parts[0]);
  if (!Number.isInteger(adt) || adt < 1) return null;
  const chd = parts.length > 1 ? Number(parts[1]) || 0 : 0;
  const inf = parts.length > 2 ? Number(parts[2]) || 0 : 0;
  return { adt, chd: Math.max(0, chd), inf: Math.max(0, inf) };
}

export function initTourData(helpers = {}) {
  return {
    type: 'tour',
    name: '',
    phone: '',
    package_name: '',
    source: '',
    date_from: '',
    date_to: null,
    pax: { adt: 2, stu: 0, chd: 0, inf: 0 },
    brief: null,
    note: null,
    _packages: helpers.tourPackages || [],
  };
}

export function buildTourSteps(data, helpers = {}) {
  const { matchSource = (v) => v, formatSourceQuestion = () => 'Lead source?' } = helpers;
  return [
    {
      key: 'name',
      ask: 'Contact name?',
      validate: (v) => (!String(v).trim() || String(v).trim().length < 2 ? 'Enter at least 2 characters.' : null),
      parse: (v) => capitalizeName(v),
      apply: (val) => { data.name = val; },
    },
    {
      key: 'phone',
      ask: 'Mobile number?',
      validate: (v) => {
        const phone = helpers.normalizePhone?.(v) || v;
        if (!phone || String(phone).replace(/\D/g, '').length < 9) return 'Enter a valid phone number.';
        return null;
      },
      parse: (v) => (helpers.normalizePhone ? helpers.normalizePhone(v) : v.trim()),
      apply: (val) => { data.phone = val; },
    },
    {
      key: 'package',
      ask: () => {
        const pkgs = data._packages.length ? data._packages : ['Vietnam', 'India', 'Dubai', 'Bangkok', 'Singapore', 'Malaysia'];
        return `Tour package?\n${pkgs.slice(0, 8).map((p, i) => `${i + 1}. ${p}`).join('  ')}`;
      },
      validate: (v) => {
        const pkgs = data._packages.length ? data._packages : ['Vietnam', 'India', 'Dubai', 'Bangkok', 'Singapore', 'Malaysia'];
        const n = Number(String(v).trim());
        if (Number.isInteger(n) && n >= 1 && n <= pkgs.length) return null;
        if (String(v).trim().length >= 2) return null;
        return 'Pick a number or type package name.';
      },
      parse: (v) => {
        const pkgs = data._packages.length ? data._packages : ['Vietnam', 'India', 'Dubai', 'Bangkok', 'Singapore', 'Malaysia'];
        const n = Number(String(v).trim());
        if (Number.isInteger(n) && n >= 1 && n <= pkgs.length) return pkgs[n - 1];
        return String(v).trim();
      },
      apply: (val) => { data.package_name = val; },
    },
    {
      key: 'pax_line',
      ask: 'Pax (ADT/CHD/INF)? — one line e.g. 2/1/0',
      hint: 'Format: adults/children/infants',
      validate: (v) => (parsePaxLine(v) ? null : 'Enter pax like 2/1/0 (≥ 1 adult).'),
      parse: (v) => parsePaxLine(v),
      apply: (val) => { data.pax = { ...data.pax, adt: val.adt, chd: val.chd, inf: val.inf }; },
    },
    {
      key: 'date_from',
      ask: 'Travel from date?',
      hint: DATE_HINT,
      validate: validateSmartDate,
      parse: (v) => parseSmartDateOnly(v),
      apply: (val) => { data.date_from = val; },
    },
    {
      key: 'date_to',
      ask: 'Travel to date?',
      hint: DATE_HINT,
      optional: true,
      validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
      parse: (v) => (isSkip(v) ? null : parseSmartDateOnly(v)),
      apply: (val) => { data.date_to = val; },
    },
    {
      key: 'source',
      ask: formatSourceQuestion,
      validate: (v) => (!matchSource(v) ? 'Pick a source.' : null),
      parse: (v) => matchSource(v),
      apply: (val) => { data.source = val; },
    },
    {
      key: 'brief',
      ask: 'Requirements / budget / preferences?',
      hint: 'One box — budget, hotel, flights, etc. Skip if none.',
      optional: true,
      parse: (v) => (isSkip(v) ? null : String(v).trim()),
      apply: (val) => { data.brief = val; data.note = val; },
    },
  ];
}

export function activeTourSteps(data, helpers) {
  const steps = buildTourSteps(data, helpers).filter((s) => !s.when || s.when(data));
  return prepareFlowSteps('tour.new', injectCustomSteps('tour.new', steps, { mode: 'new', data }));
}

export function buildTourLeadBody(data, agentId, helpers = {}) {
  const pax = { ...data.pax };
  const extra = {
    pax,
    client_brief: data.brief || null,
    tour_quote_request: {
      date_from: data.date_from || null,
      date_to: data.date_to || null,
      pax,
      special_requirements: data.brief || null,
    },
  };
  mergeCustomFieldsIntoExtra(extra, data._customFields);
  return {
    type: 'tour',
    name: data.name,
    phone: data.phone,
    package_name: data.package_name,
    source: data.source || 'Call',
    status: 'new',
    agent_id: agentId,
    inquiry_date: todayISO(),
    followup_date: helpers.defaultFollowupISO?.() || todayISO(),
    depart_date: data.date_from || null,
    return_date: data.date_to || null,
    amount: null,
    note_text: data.brief || data.note,
    extra,
  };
}

export function formatTourSummary(data) {
  const inf = data.pax.inf ? ` · ${data.pax.inf} INF` : '';
  const dates = data.date_from
    ? (data.date_to ? `${data.date_from} – ${data.date_to}` : data.date_from)
    : '';
  return [
    `Tour · ${data.package_name || '—'}${dates ? ` · ${dates}` : ''}`,
    `${data.name} · ${data.phone}`,
    `Pax: ${data.pax.adt} ADT · ${data.pax.chd} CHD${inf}`,
    data.brief ? data.brief : null,
  ].filter(Boolean).join('\n');
}

export { isSkip, DATE_HINT };
